// src/types.ts
var SUITS = ["hearts", "diamonds", "clubs", "spades"];
var RANKS = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"];
var CARD_VALUES = {
  "A": 11,
  "2": 2,
  "3": 3,
  "4": 4,
  "5": 5,
  "6": 6,
  "7": 7,
  "8": 8,
  "9": 9,
  "10": 10,
  "J": 10,
  "Q": 10,
  "K": 10
};
var JOKER_VALUE = 20;
var INITIAL_MELD_POINTS = 40;
var CARDS_PER_PLAYER = 13;
var MIN_SET_SIZE = 3;
var MAX_SET_SIZE = 4;
var MIN_RUN_SIZE = 3;
var JOKERS_PER_DECK = 3;
var DECKS_COUNT = 2;

// src/rules.ts
function createDeck() {
  const cards = [];
  for (let deckIndex = 0; deckIndex < DECKS_COUNT; deckIndex++) {
    for (const suit of SUITS) {
      for (const rank of RANKS) {
        cards.push({
          id: `${suit}-${rank}-${deckIndex}`,
          suit,
          rank,
          deckIndex
        });
      }
    }
    for (let i = 0; i < JOKERS_PER_DECK; i++) {
      cards.push({
        id: `joker-${deckIndex}-${i}`,
        suit: "joker",
        rank: "joker",
        deckIndex
      });
    }
  }
  return cards;
}
function shuffleDeck(deck) {
  const shuffled = [...deck];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
function getCardValue(card) {
  if (card.rank === "joker") {
    return JOKER_VALUE;
  }
  return CARD_VALUES[card.rank];
}
function calculateMeldValue(meld) {
  return meld.cards.reduce((sum, card) => sum + getCardValue(card), 0);
}
function calculateHandValue(hand) {
  return hand.reduce((sum, card) => sum + getCardValue(card), 0);
}
function isJoker(card) {
  return card.suit === "joker";
}
function getRankIndex(rank) {
  return RANKS.indexOf(rank);
}
function isValidSet(cards) {
  if (cards.length < MIN_SET_SIZE || cards.length > MAX_SET_SIZE) {
    return false;
  }
  const nonJokers = cards.filter((c) => !isJoker(c));
  const jokerCount = cards.length - nonJokers.length;
  if (nonJokers.length === 0) {
    return false;
  }
  const rank = nonJokers[0].rank;
  if (!nonJokers.every((c) => c.rank === rank)) {
    return false;
  }
  const suits = new Set(nonJokers.map((c) => c.suit));
  if (suits.size !== nonJokers.length) {
    return false;
  }
  return nonJokers.length + jokerCount <= 4;
}
function isValidRun(cards) {
  if (cards.length < MIN_RUN_SIZE) {
    return false;
  }
  const nonJokers = cards.filter((c) => !isJoker(c));
  const jokerCount = cards.length - nonJokers.length;
  if (nonJokers.length === 0) {
    return false;
  }
  const suit = nonJokers[0].suit;
  if (!nonJokers.every((c) => c.suit === suit)) {
    return false;
  }
  const sorted = [...nonJokers].sort(
    (a, b) => getRankIndex(a.rank) - getRankIndex(b.rank)
  );
  let jokersUsed = 0;
  for (let i = 1; i < sorted.length; i++) {
    const prevIndex = getRankIndex(sorted[i - 1].rank);
    const currIndex = getRankIndex(sorted[i].rank);
    const gap = currIndex - prevIndex - 1;
    if (gap < 0) {
      return false;
    }
    jokersUsed += gap;
  }
  if (jokersUsed > jokerCount) {
    return false;
  }
  const minIndex = getRankIndex(sorted[0].rank);
  const maxIndex = getRankIndex(sorted[sorted.length - 1].rank);
  const totalLength = maxIndex - minIndex + 1 + (jokerCount - jokersUsed);
  if (totalLength > 13) {
    return false;
  }
  return true;
}
function getMeldType(cards) {
  if (isValidSet(cards)) return "set";
  if (isValidRun(cards)) return "run";
  return null;
}
function isValidMeld(cards) {
  return getMeldType(cards) !== null;
}
function isValidInitialMeld(melds) {
  if (!melds.every(isValidMeld)) {
    return false;
  }
  const totalValue = melds.reduce((sum, meld) => {
    return sum + meld.reduce((meldSum, card) => meldSum + getCardValue(card), 0);
  }, 0);
  return totalValue >= INITIAL_MELD_POINTS;
}
function canLayOff(card, meld, position) {
  const newCards = position === "start" ? [card, ...meld.cards] : [...meld.cards, card];
  if (meld.type === "set") {
    return isValidSet(newCards);
  } else {
    return isValidRun(newCards);
  }
}
function canSwapJoker(jokerInMeld, meld, replacementCard) {
  if (!isJoker(jokerInMeld)) {
    return false;
  }
  const jokerIndex = meld.cards.findIndex((c) => c.id === jokerInMeld.id);
  if (jokerIndex === -1) {
    return false;
  }
  const newCards = [...meld.cards];
  newCards[jokerIndex] = replacementCard;
  return isValidMeld(newCards);
}
function getCardDisplay(card) {
  if (isJoker(card)) {
    return "\u{1F0CF}";
  }
  const suitSymbols = {
    hearts: "\u2665",
    diamonds: "\u2666",
    clubs: "\u2663",
    spades: "\u2660"
  };
  return `${card.rank}${suitSymbols[card.suit]}`;
}
function getSuitColor(suit) {
  if (suit === "joker") return "purple";
  if (suit === "hearts" || suit === "diamonds") return "red";
  return "black";
}
function findPossibleMelds(hand) {
  const melds = [];
  const byRank = /* @__PURE__ */ new Map();
  for (const card of hand) {
    if (isJoker(card)) continue;
    const key = card.rank;
    if (!byRank.has(key)) byRank.set(key, []);
    byRank.get(key).push(card);
  }
  for (const [, cards] of byRank) {
    if (cards.length >= MIN_SET_SIZE) {
      for (let size = MIN_SET_SIZE; size <= Math.min(cards.length, MAX_SET_SIZE); size++) {
        const combos = getCombinations(cards, size);
        for (const combo of combos) {
          if (isValidSet(combo)) {
            melds.push(combo);
          }
        }
      }
    }
  }
  const bySuit = /* @__PURE__ */ new Map();
  for (const card of hand) {
    if (isJoker(card)) continue;
    const suit = card.suit;
    if (!bySuit.has(suit)) bySuit.set(suit, []);
    bySuit.get(suit).push(card);
  }
  for (const [, cards] of bySuit) {
    if (cards.length >= MIN_RUN_SIZE) {
      const sorted = [...cards].sort(
        (a, b) => getRankIndex(a.rank) - getRankIndex(b.rank)
      );
      for (let start = 0; start < sorted.length - 2; start++) {
        for (let end = start + 2; end < sorted.length; end++) {
          const sequence = sorted.slice(start, end + 1);
          if (isValidRun(sequence)) {
            melds.push(sequence);
          }
        }
      }
    }
  }
  return melds;
}
function getCombinations(arr, size) {
  if (size === 1) return arr.map((x) => [x]);
  if (size === arr.length) return [arr];
  const result = [];
  for (let i = 0; i <= arr.length - size; i++) {
    const head = arr[i];
    const tailCombos = getCombinations(arr.slice(i + 1), size - 1);
    for (const tail of tailCombos) {
      result.push([head, ...tail]);
    }
  }
  return result;
}

// src/validation.ts
function validateAction(state, playerId, action) {
  const currentPlayer = state.players[state.currentPlayerIndex];
  if (currentPlayer.id !== playerId) {
    return { valid: false, error: "Du bist nicht am Zug" };
  }
  const hand = state.hands[playerId] || [];
  switch (action.type) {
    case "DRAW_FROM_DECK":
      return validateDrawFromDeck(state);
    case "DRAW_FROM_DISCARD":
      return validateDrawFromDiscard(state);
    case "MELD":
      return validateMeld(state, playerId, action.cards, hand);
    case "LAY_OFF":
      return validateLayOff(state, playerId, action.cardId, action.meldId, action.position, hand);
    case "DISCARD":
      return validateDiscard(state, action.cardId, hand);
    case "SWAP_JOKER":
      return validateSwapJoker(
        state,
        playerId,
        action.jokerId,
        action.meldId,
        action.replacementCardId,
        hand
      );
    default:
      return { valid: false, error: "Unbekannte Aktion" };
  }
}
function validateDrawFromDeck(state) {
  if (state.turnPhase !== "draw") {
    return { valid: false, error: "Du hast bereits gezogen" };
  }
  if (state.deck.length === 0) {
    return { valid: false, error: "Der Nachziehstapel ist leer" };
  }
  return { valid: true };
}
function validateDrawFromDiscard(state) {
  if (state.turnPhase !== "draw") {
    return { valid: false, error: "Du hast bereits gezogen" };
  }
  if (state.discardPile.length === 0) {
    return { valid: false, error: "Der Ablagestapel ist leer" };
  }
  return { valid: true };
}
function validateMeld(state, playerId, cards, hand) {
  if (state.turnPhase === "draw") {
    return { valid: false, error: "Du musst zuerst eine Karte ziehen" };
  }
  const cardIds = new Set(cards.map((c) => c.id));
  const handIds = new Set(hand.map((c) => c.id));
  for (const cardId of cardIds) {
    if (!handIds.has(cardId)) {
      return { valid: false, error: "Du hast diese Karte nicht auf der Hand" };
    }
  }
  if (!isValidMeld(cards)) {
    return { valid: false, error: "Ung\xFCltige Kombination" };
  }
  const player = state.players.find((p) => p.id === playerId);
  if (player && !player.hasInitialMeld) {
    if (!isValidInitialMeld([cards])) {
      return {
        valid: false,
        error: `Erstauslage muss mindestens 40 Punkte haben (du hast ${calculateMeldValue({ id: "", type: "set", cards, playerId })} Punkte)`
      };
    }
  }
  return { valid: true };
}
function validateLayOff(state, playerId, cardId, meldId, position, hand) {
  if (state.turnPhase === "draw") {
    return { valid: false, error: "Du musst zuerst eine Karte ziehen" };
  }
  const player = state.players.find((p) => p.id === playerId);
  if (player && !player.hasInitialMeld) {
    return {
      valid: false,
      error: "Du musst zuerst deine Erstauslage machen (mind. 40 Punkte)"
    };
  }
  const card = hand.find((c) => c.id === cardId);
  if (!card) {
    return { valid: false, error: "Du hast diese Karte nicht auf der Hand" };
  }
  const meld = state.melds.find((m) => m.id === meldId);
  if (!meld) {
    return { valid: false, error: "Meld nicht gefunden" };
  }
  if (!canLayOff(card, meld, position)) {
    return { valid: false, error: "Diese Karte passt nicht an dieses Meld" };
  }
  return { valid: true };
}
function validateDiscard(state, cardId, hand) {
  if (state.turnPhase === "draw") {
    return { valid: false, error: "Du musst zuerst eine Karte ziehen" };
  }
  const card = hand.find((c) => c.id === cardId);
  if (!card) {
    return { valid: false, error: "Du hast diese Karte nicht auf der Hand" };
  }
  return { valid: true };
}
function validateSwapJoker(state, playerId, jokerId, meldId, replacementCardId, hand) {
  if (state.turnPhase === "draw") {
    return { valid: false, error: "Du musst zuerst eine Karte ziehen" };
  }
  const player = state.players.find((p) => p.id === playerId);
  if (player && !player.hasInitialMeld) {
    return {
      valid: false,
      error: "Du musst zuerst deine Erstauslage machen (mind. 40 Punkte)"
    };
  }
  const replacementCard = hand.find((c) => c.id === replacementCardId);
  if (!replacementCard) {
    return { valid: false, error: "Du hast diese Karte nicht auf der Hand" };
  }
  const meld = state.melds.find((m) => m.id === meldId);
  if (!meld) {
    return { valid: false, error: "Meld nicht gefunden" };
  }
  const jokerInMeld = meld.cards.find((c) => c.id === jokerId);
  if (!jokerInMeld) {
    return { valid: false, error: "Joker nicht in diesem Meld" };
  }
  if (!isJoker(jokerInMeld)) {
    return { valid: false, error: "Das ist kein Joker" };
  }
  if (!canSwapJoker(jokerInMeld, meld, replacementCard)) {
    return { valid: false, error: "Diese Karte kann den Joker nicht ersetzen" };
  }
  return { valid: true };
}
function applyAction(state, playerId, action) {
  const newState = { ...state };
  newState.hands = { ...state.hands };
  newState.melds = [...state.melds];
  newState.lastActionAt = Date.now();
  const hand = [...state.hands[playerId] || []];
  switch (action.type) {
    case "DRAW_FROM_DECK": {
      const card = newState.deck.pop();
      hand.push(card);
      newState.hands[playerId] = hand;
      newState.deck = [...newState.deck];
      newState.turnPhase = "play";
      break;
    }
    case "DRAW_FROM_DISCARD": {
      const card = newState.discardPile.pop();
      hand.push(card);
      newState.hands[playerId] = hand;
      newState.discardPile = [...newState.discardPile];
      newState.turnPhase = "play";
      break;
    }
    case "MELD": {
      const cardIds = new Set(action.cards.map((c) => c.id));
      const remainingHand = hand.filter((c) => !cardIds.has(c.id));
      newState.hands[playerId] = remainingHand;
      const meldType = getMeldType(action.cards);
      const newMeld = {
        id: `meld-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        type: meldType,
        cards: action.cards,
        playerId
      };
      newState.melds = [...newState.melds, newMeld];
      newState.players = state.players.map(
        (p) => p.id === playerId ? { ...p, hasInitialMeld: true } : p
      );
      if (remainingHand.length === 0) {
        newState.phase = "ended";
        newState.winnerId = playerId;
      }
      break;
    }
    case "LAY_OFF": {
      const card = hand.find((c) => c.id === action.cardId);
      const remainingHand = hand.filter((c) => c.id !== action.cardId);
      newState.hands[playerId] = remainingHand;
      newState.melds = state.melds.map((m) => {
        if (m.id === action.meldId) {
          const newCards = action.position === "start" ? [card, ...m.cards] : [...m.cards, card];
          return { ...m, cards: newCards };
        }
        return m;
      });
      if (remainingHand.length === 0) {
        newState.phase = "ended";
        newState.winnerId = playerId;
      }
      break;
    }
    case "DISCARD": {
      const card = hand.find((c) => c.id === action.cardId);
      const remainingHand = hand.filter((c) => c.id !== action.cardId);
      newState.hands[playerId] = remainingHand;
      newState.discardPile = [...state.discardPile, card];
      if (remainingHand.length === 0) {
        newState.phase = "ended";
        newState.winnerId = playerId;
      } else {
        newState.currentPlayerIndex = (state.currentPlayerIndex + 1) % state.players.length;
        newState.turnPhase = "draw";
      }
      break;
    }
    case "SWAP_JOKER": {
      const replacementCard = hand.find((c) => c.id === action.replacementCardId);
      const remainingHand = hand.filter((c) => c.id !== action.replacementCardId);
      const meld = state.melds.find((m) => m.id === action.meldId);
      const joker = meld.cards.find((c) => c.id === action.jokerId);
      remainingHand.push(joker);
      newState.hands[playerId] = remainingHand;
      newState.melds = state.melds.map((m) => {
        if (m.id === action.meldId) {
          const newCards = m.cards.map(
            (c) => c.id === action.jokerId ? replacementCard : c
          );
          return { ...m, cards: newCards };
        }
        return m;
      });
      break;
    }
  }
  return newState;
}
export {
  CARDS_PER_PLAYER,
  CARD_VALUES,
  DECKS_COUNT,
  INITIAL_MELD_POINTS,
  JOKERS_PER_DECK,
  JOKER_VALUE,
  MAX_SET_SIZE,
  MIN_RUN_SIZE,
  MIN_SET_SIZE,
  RANKS,
  SUITS,
  applyAction,
  calculateHandValue,
  calculateMeldValue,
  canLayOff,
  canSwapJoker,
  createDeck,
  findPossibleMelds,
  getCardDisplay,
  getCardValue,
  getMeldType,
  getRankIndex,
  getSuitColor,
  isJoker,
  isValidInitialMeld,
  isValidMeld,
  isValidRun,
  isValidSet,
  shuffleDeck,
  validateAction
};
//# sourceMappingURL=index.js.map